import { useCallback, useState } from "react";
import { getImageFileName } from "domains/assets/utils";
import { getAssetType } from "domains/assets/utils/getIsType";
import {
  loadBase64FromUrl,
  loadDataFromUrl,
} from "domains/image/methods/loadDataFromUrl";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  GetModelsInferencesByModelIdAndInferenceIdApiResponse,
  useLazyGetAssetsByAssetIdQuery,
  usePostDownloadAssetsMutation,
} from "infra/api/generated/api";
import _ from "lodash";
import mime from "mime-types";

export type DOWNLOAD_FORMATS =
  | "jpeg"
  | "png"
  | "webp"
  | "svg"
  | "raw"
  | "texture";

interface UseAssetDownloadProps {
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  model?: GetModelsByModelIdApiResponse["model"];
  inference?: GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"];
  trackingEvent: string;
}

export function useAssetDownload({
  asset,
  model,
  inference,
  trackingEvent,
}: UseAssetDownloadProps) {
  const { selectedProject } = useTeamContext();
  const { errorToast, successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { showLimitModal } = usePlanContext();
  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const [triggerDownload] = usePostDownloadAssetsMutation();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadMaps = useCallback(async () => {
    if (!asset) return;

    setIsDownloading(true);
    try {
      await triggerDownload({
        projectId: selectedProject.id,
        body: {
          query: {
            assetIds: [asset.id],
            modelIds: [],
            inferenceIds: [],
          },
          options: {
            flat: true,
            fileNameTemplate: "",
          },
        },
      }).unwrap();
      successToast({
        title: "Download initiated",
        description:
          "A download link will be available via email and in the Notification tab.",
      });
    } catch (error: any) {
      handleApiError(error, "There was an error initiating your download", {
        quota: () => {
          if (_.get(error, "data.details.remainingSeconds")) {
            showLimitModal("planCooldown", {
              timeout: error.data.details.remainingSeconds,
              type: "download",
            });
          }
        },
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    asset,
    handleApiError,
    selectedProject.id,
    showLimitModal,
    successToast,
    triggerDownload,
  ]);

  const handleDownload = useCallback(
    async (initialFormat: DOWNLOAD_FORMATS) => {
      if (!asset) return;

      const isTexture = asset.metadata.type === "texture";
      const is3d = asset.metadata.kind === "3d";
      const isVideo = asset.metadata.kind === "video";

      const format = isTexture
        ? "texture"
        : is3d || isVideo
        ? "raw"
        : initialFormat;

      if (format === "texture") {
        void downloadMaps();
        return;
      }

      const assetType = getAssetType(asset).singular;

      setIsDownloading(true);
      if (format === "raw") {
        const dataUrl = await loadDataFromUrl(asset.url);
        if (dataUrl) {
          const link = document.createElement("a");
          link.href = dataUrl;
          link.download = `${asset.id}.${mime.extension(asset.mimeType)}`;
          link.click();
          Track(trackingEvent, {
            assetId: asset.id,
            format,
          });
        } else {
          errorToast({
            title: `Error downloading ${assetType}`,
          });
        }
        setIsDownloading(false);
        return;
      }

      let assetUrl = asset.url;
      if (!assetUrl.includes("/assets-transform")) {
        const transformedAsset = await getAssetById({
          projectId: selectedProject.id,
          assetId: asset.id,
        });
        if (transformedAsset.data?.asset) {
          assetUrl = transformedAsset.data.asset.url;
        }
      }
      ["quality", "format", "width", "height"].forEach((key) => {
        const regex = new RegExp(`[?&]${key}=[^&]*`);
        assetUrl = assetUrl.replace(regex, "");
      });
      const newUrl = `${assetUrl}&format=${format}`;

      const base64 = await loadBase64FromUrl(newUrl);
      if (base64) {
        const link = document.createElement("a");
        link.href = base64;
        link.download = getImageFileName({
          model,
          inference,
          asset,
          options: {
            format,
          },
        });
        link.click();
        Track(trackingEvent, {
          assetId: asset.id,
          format,
        });
      } else {
        errorToast({
          title: `Error downloading ${assetType}`,
        });
      }
      setIsDownloading(false);
    },
    [
      asset,
      getAssetById,
      selectedProject.id,
      model,
      inference,
      errorToast,
      trackingEvent,
      downloadMaps,
    ]
  );

  return {
    handleDownload,
    isDownloading,
  };
}
